import React, { useEffect, useState } from "react";

import { KnowledgeAttachmentPicker } from "./KnowledgeAttachmentPicker";
import type { KnowledgeItemData } from "../lib/api";
import type { CompanyResearchIntentType } from "../lib/companyResearch";
import { getKnowledgeItemDisplayName } from "../lib/knowledge";

const COMPANY_RESEARCH_URL_KNOWLEDGE_TYPES = ["pdf", "executive_memo", "url"] as const;

export function shouldPromptForSelfCompanyUrl(intentType: CompanyResearchIntentType) {
  return intentType === "sales" || intentType === "partnership";
}

export function supportsUrlKnowledgeForCompanyResearch(
  intentType: CompanyResearchIntentType | null
): boolean {
  return intentType === "sales" || intentType === "partnership";
}

function projectCompanyResearchKnowledgeSelection(
  item: KnowledgeItemData | null
): KnowledgeItemData | null {
  if (!item) return null;

  return {
    id: item.id,
    type: item.type,
    title: getKnowledgeItemDisplayName(item),
    status: item.status,
    original_filename: null,
    mime_type: null,
    size_bytes: null,
    page_count: item.page_count,
    text_extractable: item.text_extractable,
    ai_processing_allowed: item.ai_processing_allowed,
    ai_preparation_status: item.ai_preparation_status ?? null,
    ai_preparation_failure_code: null,
    can_retry_ai_preparation: false,
    failure_reason: null,
    failure_code: null,
    failure_stage: null,
    failure_message: null,
    can_retry: false,
    summary: null,
    has_summary: item.has_summary,
    summarized_at: item.summarized_at ?? null,
    domain: item.type === "url" ? item.domain ?? null : null,
    created_at: null,
    updated_at: null,
  };
}

type CompanyResearchSelfUrlModalProps = {
  intentType: CompanyResearchIntentType | null;
  onClose: () => void;
  onSubmit: (selfCompanyUrl: string | null, selfCompanyKnowledgeItemId: string | null) => void | Promise<void>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
};

export function CompanyResearchSelfUrlModal({
  intentType,
  onClose,
  onSubmit,
  isSubmitting = false,
  errorMessage = null,
}: CompanyResearchSelfUrlModalProps) {
  const [selfCompanyUrl, setSelfCompanyUrl] = useState("");
  const [selectedKnowledgeItem, setSelectedKnowledgeItem] = useState<KnowledgeItemData | null>(null);

  useEffect(() => {
    if (!intentType) {
      setSelfCompanyUrl("");
      setSelectedKnowledgeItem(null);
    }
  }, [intentType]);

  if (!intentType) return null;

  const allowsUrlKnowledge = supportsUrlKnowledgeForCompanyResearch(intentType);
  const knowledgeTypeDescription = allowsUrlKnowledge
    ? "PDF、Webページ、または社長メモから"
    : "PDFまたは社長メモから";
  const knowledgeDescription = intentType === "partnership"
    ? `${knowledgeTypeDescription}自社の事業や強みが分かる資料を1件選ぶと、相手企業との協業可能性を考える際に利用します。`
    : `${knowledgeTypeDescription}自社サービスや実績が分かる資料を1件選ぶと、相手企業に合う提案の切り口を考える際に利用します。`;

  const submitWithSelfInfo = () => {
    void onSubmit(selfCompanyUrl.trim() || null, selectedKnowledgeItem?.id ?? null);
  };

  const submitWithoutSelfInfo = () => {
    void onSubmit(null, null);
  };

  const close = () => {
    setSelfCompanyUrl("");
    setSelectedKnowledgeItem(null);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="self-company-url-title"
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/35 px-3 py-4 sm:px-4 sm:py-6"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#EADBC8] bg-white shadow-[0_24px_60px_rgba(74,62,57,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#F2E6DA] px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 id="self-company-url-title" className="break-words text-base font-black text-[#5D4D46]">
              自社情報を追加できます
            </h2>
            <p className="mt-3 text-sm font-bold leading-6 text-[#6B5851]">
              営業・協業分析には自社情報があると精度が上がります。
              自社URLやナレッジライブラリの自社資料を任意で利用できます。
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={isSubmitting}
            className="shrink-0 rounded-full border border-[#EADBC8] bg-white px-3 py-1 text-xs font-black text-[#8A746A] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
          >
            閉じる
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          <label className="block">
            <span className="text-xs font-black text-[#5D4D46]">自社URL 任意</span>
            <input
              type="url"
              value={selfCompanyUrl}
              onChange={(event) => setSelfCompanyUrl(event.target.value)}
              disabled={isSubmitting}
              className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6] disabled:cursor-not-allowed disabled:opacity-70"
              placeholder="https://example.co.jp/"
            />
          </label>

          <KnowledgeAttachmentPicker
            value={selectedKnowledgeItem}
            onChange={(item) => setSelectedKnowledgeItem(projectCompanyResearchKnowledgeSelection(item))}
            mode="companyResearch"
            allowedTypes={allowsUrlKnowledge
              ? COMPANY_RESEARCH_URL_KNOWLEDGE_TYPES
              : undefined}
            showUpload={false}
            allowDelete={false}
            compact
            heading="保存済みナレッジを使う（任意）"
            description={knowledgeDescription}
            usageNote={null}
            emptyMessage="ナレッジは未選択です。自社URLのみ、または自社情報なしでも分析できます。"
            selectedLabel="選択中のナレッジ"
            detachLabel="選択解除"
            pollingMessage="ナレッジを読み取り中です。完了後に選択できます。"
            unavailableFallbackMessage="このナレッジはまだ利用できません。別のナレッジを選ぶか、選択せずに進めてください。"
          />

          {errorMessage ? (
            <p role="alert" className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <div className="grid gap-3 border-t border-[#F2E6DA] bg-white px-4 py-4 sm:grid-cols-2 sm:px-5">
          <button
            type="button"
            onClick={submitWithSelfInfo}
            disabled={isSubmitting}
            className="min-h-[44px] rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "開始中..." : "この内容で分析"}
          </button>
          <button
            type="button"
            onClick={submitWithoutSelfInfo}
            disabled={isSubmitting}
            className="min-h-[44px] rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
          >
            自社情報なしで分析
          </button>
        </div>
      </div>
    </div>
  );
}
